import { FC, useCallback, useMemo } from 'react';
import { DecimalFormat } from '@tempusfinance/decimal';
import { ButtonWrapper } from '@tempusfinance/common-ui';
import Logo from '../../../Logo';
import Typography from '../../../Typography';
import { CurrencyInputToken } from '../../CurrencyInputTypes';
import { getConfigManager } from '../../../../../config';

import './TokenSelectorItem.scss';

interface TokenSelectorItemProps {
  token: CurrencyInputToken;
  onTokenSelected: (token: CurrencyInputToken) => void;
}

const TokenSelectorItem: FC<TokenSelectorItemProps> = ({ token, onTokenSelected }) => {
  const poolConfigs = useMemo(() => Object.values(getConfigManager().getConfig().pools), []);

  const uiDecimal = useMemo(
    () =>
      poolConfigs.reduce((result, config) => {
        if (config.pair.tokenA.address === token.address) {
          return config.pair.tokenA.uiDecimals;
        } else if (config.pair.tokenB.address === token.address) {
          return config.pair.tokenB.uiDecimals;
        }

        return result;
      }, 4),
    [poolConfigs, token.address],
  );

  // token.price is resolved by the parent TokenSelector via useTokenPriceResolver,
  // which handles all fallback logic including AVNU-only tokens.
  const tokenValue = useMemo(() => {
    return token.price?.mul(token.balance ?? 0) ?? null;
  }, [token.price, token.balance]);

  const handleTokenSelected = useCallback(() => {
    onTokenSelected(token);
  }, [onTokenSelected, token]);

  const formattedTokenBalance = useMemo(() => {
    if (!token.balance) {
      return null;
    }

    return DecimalFormat.format(token.balance, {
      style: 'decimal',
      fractionDigits: uiDecimal,
      pad: true,
    });
  }, [token.balance, uiDecimal]);

  const showBalance = useMemo(() => {
    return token.balance && token.balance.gt(0);
  }, [token.balance]);

  const formattedTokenValue = useMemo(() => {
    if (!tokenValue || tokenValue.isZero()) {
      return null;
    }

    return (
      '~' +
      DecimalFormat.format(tokenValue, {
        style: 'currency',
        currency: '$',
        fractionDigits: 2,
        pad: true,
      })
    );
  }, [tokenValue]);

  return (
    <ButtonWrapper className="nostra__tokenSelector__itemBtn" onClick={handleTokenSelected}>
      <div className="nostra__tokenSelector__item">
        <div className="nostra__tokenSelector__itemInfo">
          <div className="nostra__tokenSelector__itemLogo">
            {token.logoUri && (
              <img
                src={token.logoUri}
                width={32}
                height={32}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {token.logoType && <Logo type={token.logoType} />}
          </div>
          <div className="nostra__tokenSelector__itemLabel">
            <Typography variant="table-body-lg" color="text-primary" weight="semi-bold">
              {token.symbol}
            </Typography>
            <Typography variant="body-sm" color="text-supporting">
              {token.name}
            </Typography>
          </div>
        </div>
        {showBalance && formattedTokenBalance && (
          <div className="nostra__tokenSelector__itemValue">
            <Typography variant="table-body-lg" color="text-primary" weight="semi-bold">
              {formattedTokenBalance}
            </Typography>
            <Typography variant="table-body-sm" color="text-supporting">
              {formattedTokenValue}
            </Typography>
          </div>
        )}
      </div>
    </ButtonWrapper>
  );
};
export default TokenSelectorItem;
